import type { TransferDetailDto } from '@matix/api-client';
import { printReport, type ReportPaper } from '../reports/reportPrint';
import { formatReceiptDateTime } from '../receipts/helpers';

type Lang = 'en' | 'ar';

/**
 * The transfer slip — printed on the roll behind the counter or on A4 in the
 * back office, through the same pipe the reports use.
 *
 * BILINGUAL ON PURPOSE. The paper travels with the goods from one branch to
 * another, and the person unpacking the box may read either script; every
 * label carries both, and every item shows both names when it has them.
 */
export function printTransferSlip(d: TransferDetailDto, lang: Lang, paper: ReportPaper = 'roll'): void {
  const L = (en: string, ar: string): string => `${escapeHtml(en)} / ${escapeHtml(ar)}`;
  const status = {
    pending: L('Pending', 'قيد الانتظار'),
    received: L('Received', 'تم الاستلام'),
    cancelled: L('Cancelled', 'ملغي'),
  }[d.status];
  const store = (s: { name: string; name_ar: string | null }): string =>
    s.name_ar && s.name_ar !== s.name ? `${escapeHtml(s.name)} / ${escapeHtml(s.name_ar)}` : escapeHtml(s.name);

  const meta: Array<[string, string]> = [
    [L('From store', 'من مخزن'), store(d.from_store)],
    [L('To store', 'إلى مخزن'), store(d.to_store)],
    [L('Date', 'التاريخ'), d.created_at ? escapeHtml(formatReceiptDateTime(d.created_at)) : '—'],
    [L('Requested by', 'بواسطة'), escapeHtml(d.created_by_name ?? '—')],
    [L('Status', 'الحالة'), status],
  ];
  if (d.reference_no) meta.push([L('Reference', 'المرجع'), escapeHtml(d.reference_no)]);
  if (d.received_entry_no != null) {
    meta.push([
      L('Goods document', 'سند التحويل'),
      `#${d.received_entry_no}${d.received_at ? ` · ${escapeHtml(formatReceiptDateTime(d.received_at))}` : ''}`,
    ]);
  }
  if (d.notes) meta.push([L('Notes', 'ملاحظات'), escapeHtml(d.notes)]);

  const metaHtml = `<table class="tr-meta"><tbody>${meta
    .map(([k, v]) => `<tr><th>${k}</th><td>${v}</td></tr>`)
    .join('')}</tbody></table>`;

  const rows = d.lines
    .map((l, i) => {
      const name =
        l.name_ar && l.name_ar !== l.name
          ? `${escapeHtml(l.name ?? '')}<br><span class="ar">${escapeHtml(l.name_ar)}</span>`
          : escapeHtml(l.name ?? l.name_ar ?? l.code);
      const unit = (lang === 'ar' ? l.unit_name_ar : l.unit_name) || l.unit_name || '';
      return `<tr>
        <td class="num">${i + 1}</td>
        <td>${name}<div class="code">${escapeHtml(l.code)}</div></td>
        <td class="num">${fmtQty(l.qty)}</td>
        <td>${escapeHtml(unit)}</td>
      </tr>`;
    })
    .join('');
  const totalQty = d.lines.reduce((s, l) => s + l.qty, 0);

  const itemsHtml = `<table>
    <thead><tr>
      <th class="num">#</th>
      <th>${L('Item', 'الصنف')}</th>
      <th class="num">${L('Qty', 'الكمية')}</th>
      <th>${L('Unit', 'الوحدة')}</th>
    </tr></thead>
    <tbody>${rows}</tbody>
    <tfoot><tr><td colspan="2">${L('Total items', 'إجمالي الأصناف')}: ${d.lines.length}</td><td class="num">${fmtQty(totalQty)}</td><td></td></tr></tfoot>
  </table>
  <div class="tr-sign">
    <div><div class="line"></div>${L('Handed over by', 'تسليم')}</div>
    <div><div class="line"></div>${L('Received by', 'استلام')}</div>
  </div>
  <style>
    .tr-meta { margin-bottom: 8px; }
    .tr-meta th { width: 38%; text-align: start; font-weight: 700; }
    .code { font-size: 0.85em; color: #444; }
    .ar { font-size: 0.95em; }
    .tr-sign { display: flex; gap: 16px; margin-top: 18px; font-size: 0.9em; text-align: center; }
    .tr-sign > div { flex: 1; }
    .tr-sign .line { border-top: 1px solid #000; margin: 22px 0 4px; }
  </style>`;

  const title = `${lang === 'ar' ? 'تحويل مخزني' : 'Store transfer'} #${d.entry_no}`;
  const subtitle = lang === 'ar' ? `Store transfer request #${d.entry_no}` : `طلب تحويل مخزني رقم ${d.entry_no}`;
  printReport(title, subtitle, metaHtml + itemsHtml, paper);
}

function fmtQty(n: number): string {
  return n.toLocaleString(undefined, { maximumFractionDigits: 3 });
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' } as Record<string, string>
  )[c]!);
}
